import { MAX_COMBINED_FACET } from "../../constants/filters-and-facets.constant";
import { MissingFacetResult } from "../../constants/missing-facet-context.constant";
import {
    type CombinedFacets,
    FH_LOCATION_REGEX,
    FH_RANGE_PRICE_PARAM,
    type GridFilter,
    type GridFilterGroup,
    type PriceRange,
    SelectedFilter,
} from "../../store/grid.store.model";

import { isSwitchFilterActive } from "../switch-filter/switch-filter.helper";

const getFhParameterMatcher = (parameter: string) =>
    new RegExp(`${parameter}=([^&]+).*$`);
const getUniqueFhParameterMatcher = (parameter: string) =>
    new RegExp(`${parameter}=([^&]+)[&]?`);
// For example, get /price_be>360.0/price_be<25526.0/ from fh_location decoded URI.
const getPriceinLocationMatcher = new RegExp("(/price_be).*?((/)(?!price_be))");

const facetOrderConfiguration: Record<string, number> = {
    facet_category: 1,
    facet_line: 2,
    facet_material: 3,
    facet_finish: 4,
    facet_color: 5,
    facet_heel_height: 6,
    facet_model_size: 7,
    facet_gold_type: 8,
    facet_movement: 9,
    facet_complication: 10,
    facet_univers: 11,
    facet_volume: 12,
    facet_emotion_olfactive: 13,
    facet_cut: 14,
    facet_theme: 15,
};

/**
 * Update query params string with given parameter
 * @param parameterName given parameter name
 * @param parameterValue given parameter value
 * @param fhParameters query string to update
 * @returns updated query string
 */
export const updateFhParameters = (
    parameterName: string,
    parameterValue: string,
    fhParameters: string = "",
): string => {
    const newFhParameter = `${encodeURIComponent(
        parameterName,
    )}=${encodeURIComponent(parameterValue)}`;

    if (fhParameters.length === 0) {
        return newFhParameter;
    }
    const parameter = getParameterFromFhParameter(
        FH_LOCATION_REGEX,
        fhParameters,
    );

    // Case some filter are in fh_location - avoid to erase them while updating fh_price_range.
    if (
        parameterName === FH_RANGE_PRICE_PARAM &&
        getParameterFromFhParameter(FH_RANGE_PRICE_PARAM, fhParameters) &&
        parameter
    ) {
        // Remove price filter inside the fh_location param while keeping others
        const newFhLocation = `${encodeURIComponent(
            FH_LOCATION_REGEX,
        )}=${encodeURIComponent(
            parameter.replace(getPriceinLocationMatcher, "/"),
        )}`;
        const updatedFhParameters: string = fhParameters.replace(
            getFhParameterMatcher(parameterName),
            newFhParameter,
        );
        return `${updatedFhParameters}&${newFhLocation}`;
    }

    if (getParameterFromFhParameter(parameterName, fhParameters)) {
        return fhParameters.replace(
            getFhParameterMatcher(parameterName),
            newFhParameter,
        );
    }

    return `${fhParameters}&${newFhParameter}`;
};

/**
 * Retrieve parameter value into a query param string
 * @param parameterName given parameter name
 * @param fhParameters query string to update
 * @returns value of given parameter
 */
export const getParameterFromFhParameter = (
    parameterName: string,
    fhParameters: string = "",
): string | undefined => {
    const matchValue = fhParameters.match(getFhParameterMatcher(parameterName));

    if (!matchValue) {
        return undefined;
    }

    return decodeURIComponent(matchValue[1]);
};

/**
 * Deserialize price range from URL format
 * @param pricesString serialized price range param
 * @returns deserialized price range
 */
export const getPriceRangeFromUrl = (
    pricesString: string,
): PriceRange | undefined => {
    if (!pricesString) {
        return undefined;
    }
    const prices = pricesString.split(",");
    const [valueMin, valueMinCurrent, valueMaxCurrent, valueMax] = prices;
    return {
        valueMax: Number.parseInt(valueMax, 10),
        valueMaxCurrent: Number.parseInt(valueMaxCurrent, 10),
        valueMin: Number.parseInt(valueMin, 10),
        valueMinCurrent: Number.parseInt(valueMinCurrent, 10),
    };
};

/**
 * Serialize price range in URL format
 * @param prices given price range
 * @returns serialized price range
 */
export const getUrlFormattedPrices = (prices: PriceRange): string => {
    if (!prices) {
        return "";
    }
    return `${prices.valueMin},${prices.valueMinCurrent},${prices.valueMaxCurrent},${prices.valueMax}`;
};

/**
 * Remove unnecessary params into FH query params string
 * @param fhParameters FH params to clean
 * @param parameterToClean restrict clean to given parameters
 * @returns cleaned FH params
 */
export const cleanUrlParameter = (
    fhParameters: string,
    parameterToClean: string,
): string =>
    fhParameters.replace(getUniqueFhParameterMatcher(parameterToClean), "");

export const getFacetsQueryParameters = (
    combinedFacets: CombinedFacets[],
): Array<[string, string]> =>
    combinedFacets
        .filter(({ facetName, facetNameValue }) => facetName && facetNameValue)
        .map(({ facetName, facetNameValue }) => [facetName, facetNameValue]);

export const getSelectedFilters = (
    filterGroups: GridFilterGroup[],
): SelectedFilter[] => {
    // Impl. note: presentation "switch" has special behavior, as the "off" state
    // may be represented in two ways:
    // - no selected filter
    //      --> in this case there is no special behavior
    // - "off" filter is selected
    //      --> in this case, contrary to other presentations,
    //          we want to consider it is NOT selected
    const listOfAcceptedPresentationExceptSwitch: Set<string> = new Set<string>(
        ["list", "colors", "grid"],
    );

    return (
        filterGroups
            .filter(
                (fg: GridFilterGroup) =>
                    listOfAcceptedPresentationExceptSwitch.has(
                        fg.presentation,
                    ) || isSwitchFilterActive(fg),
            )
            // oxlint-disable-next-line unicorn/no-array-reduce
            .reduce(
                (accumulator: GridFilter[], item: GridFilterGroup) => [
                    ...accumulator,
                    ...item.filters,
                ],
                [],
            )
            .filter((item: GridFilter) => item.selected)
            .map((gridFilter) => ({
                name: gridFilter.name,
                value: gridFilter.value,
            }))
    );
};

export const checkMissingFacet = (
    selectedFilters: SelectedFilter[],
    combinedFacets: CombinedFacets[],
    facetsWithSeoPriority: Record<string, number>,
): MissingFacetResult => {
    const isMissingFacet = selectedFilters?.length < combinedFacets?.length;

    const filteredValues: CombinedFacets[] = sortFacetsFromState(
        (combinedFacets ?? []).filter((value) =>
            selectedFilters.some(
                (filter) => filter.value === value.facetNameValue,
            ),
        ),
        facetsWithSeoPriority,
    );

    return {
        isMissingFacet,
        filteredValues,
    };
};

export const updateFiltersCrawlability = (
    filterGroups: GridFilterGroup[],
    combinedFacets: CombinedFacets[],
    urlParameters?: string,
): GridFilterGroup[] => {
    const isFilteredGridCase =
        urlParameters === "" || urlParameters === undefined;
    const isMaximumFacetsAchieved =
        filterGroups && combinedFacets.length >= MAX_COMBINED_FACET;

    const shouldDisableCrawlability =
        !isFilteredGridCase || isMaximumFacetsAchieved;

    return filterGroups.map((filterGroup) => {
        const filters = filterGroup.filters.map((filter) => ({
            ...filter,
            facetIsCrawlable:
                !shouldDisableCrawlability && filter.facetIsCrawlable,
        }));
        return {
            ...filterGroup,
            filters,
        };
    });
};

// Check if urlParams contains lister: for product grid or search for search page
export const hasFacetFilterInUrlParameters = (fhLocation: string) => {
    const regex = /fh_refview=(search|lister)/;
    return regex.test(fhLocation);
};

const compareByPriority = (
    keyA: string,
    keyB: string,
    facetsWithSeoPriority: Record<string, number>,
    fallback: (keyA: string, keyB: string) => number,
): number => {
    const a = facetsWithSeoPriority[keyA];
    const b = facetsWithSeoPriority[keyB];

    if (a === undefined && b === undefined) {
        return fallback(keyA, keyB);
    }

    if (a === undefined) {
        return 1;
    }

    if (b === undefined) {
        return -1;
    }

    return a - b;
};

export const sortFacetsFromState = (
    combinedFacets: CombinedFacets[],
    facetsWithSeoPriority: Record<string, number>,
) => {
    const hasSeoFacets = Object.keys(facetsWithSeoPriority).length > 0;

    return [...combinedFacets].sort((a, b) =>
        compareByPriority(
            a.facetName,
            b.facetName,
            hasSeoFacets ? facetsWithSeoPriority : {},
            (keyA, keyB) =>
                facetOrderConfiguration[keyA] - facetOrderConfiguration[keyB],
        ),
    );
};

export const sortQueryParameters = (
    facetQueryParameter: Record<string, string>,
    facetsWithSeoPriority?: Record<string, number>,
) => {
    const hasSeoFacets =
        facetsWithSeoPriority && Object.keys(facetsWithSeoPriority).length > 0;

    return Object.fromEntries(
        [...Object.entries(facetQueryParameter)].sort(([keyA], [keyB]) =>
            compareByPriority(
                keyA,
                keyB,
                hasSeoFacets ? facetsWithSeoPriority : {},
                (kA, kB) =>
                    facetOrderConfiguration[kA] - facetOrderConfiguration[kB],
            ),
        ),
    );
};

export const buildFacetsWithSeoPriority = (
    filterGroups: GridFilterGroup[],
): Record<string, number> => {
    const facetsWithSeoPriority: Record<string, number> = {};
    filterGroups
        .filter(
            (filterGroup) =>
                filterGroup.facetUrlParameterName?.trim() &&
                filterGroup.seoPriority !== undefined &&
                filterGroup.seoPriority !== null,
        )
        .forEach((filterGroup) => {
            if (filterGroup.seoPriority) {
                facetsWithSeoPriority[filterGroup.facetUrlParameterName] =
                    filterGroup.seoPriority;
            }
        });

    return facetsWithSeoPriority;
};
